import { useLiveWeather } from '../hooks/useLiveWeather'
import { currentDisplayTempC, formatFahrenheit } from '../lib/displayWeather'
import usePrimaryWear from '../hooks/usePrimaryWear'
import {primaryHero,currentPrimaryWear} from '../lib/primaryWear'
import PrimaryWearControl from '../components/home/PrimaryWearControl'
import PrimaryWearCard from '../components/home/PrimaryWearCard'
import { homeHistoryLogs } from '../lib/dailyWearHistory'
import DailyWearHistory from '../components/home/DailyWearHistory'
import { checkinTimeline } from '../lib/productIntegration'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { createCatalogSotdState, saveCatalogSotd } from '../lib/catalogSotd'
import { sessionsForDay } from '../lib/wearSessions'
import ContextPill from '../components/ui/ContextPill'
import { CollectionSearchInput } from '../components/ui/CollectionDatalist'
import CatalogSearchHierarchy from '../components/add/CatalogSearchHierarchy'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { useCollection } from '../hooks/useCollection'
import { useCatalogSearch } from '../hooks/useCatalogSearch'
import { useWearLog } from '../hooks/useWearLog'
import { buildWearLogEntry } from '../lib/wearLogContext'
import { useLayeringRecommendations } from '../hooks/useLayeringRecommendations'
import FragranceImage from '../components/collection/FragranceImage'
import WearQuickCapture from '../components/collection/WearQuickCapture'
import LayeringPickerModal from '../components/layering/LayeringPickerModal'
import HomeMemorySheet from '../components/home/HomeMemorySheet'
import MemoryAnniversaryCard from '../components/home/MemoryAnniversaryCard'
import { useMemoryAnniversaries } from '../hooks/useMemoryAnniversaries'
import { anniversariesForDay } from '../lib/memoryAnniversary'
import ConcentrationPickerSheet from '../components/home/ConcentrationPickerSheet'
import ProjectionCheckin from '../components/layering/ProjectionCheckin'
import { MobileScrollRegion } from '../components/layout/MobileChrome'
import GymWearSummary from '../components/home/GymWearSummary'
import HomeGateways from '../components/home/HomeGateways'
import TileTaxonomyRow from '../components/home/TileTaxonomyRow'
import { exactCurrentGymWear } from '../lib/gymQuickWear'
import { buildTileTaxonomy } from '../lib/tileTaxonomy'
import { complimentCountForLog } from '../lib/complimentEvents'
import ShareCardSheet from '../components/share/ShareCardSheet'
import { Button, Spinner } from '../components/ui'
import { matchCollectionForWear, groupCollectionByLine } from '../lib/collectionWearSearch'
import {
  appliedAtLabel,
  concentrationLabel,
  displayDate,
  todayISO,
  eveningRecommendation,
  sotdPromptCopy,
  statsForFragrance,
  wearLabelText,
} from '../lib/homeNav'
import { selectSotdHero, traceSotdHeroRender } from '../lib/sotdHero'
import { isGymWearLog } from '../lib/gymShelf'
import { acceptWearSearchSelection } from '../lib/wearSearchSelection'
import {
  abandonWearFunnelSession,
  createWearFunnelSession,
  emitWearFunnelEvent,
  isWearFunnelSessionActive,
} from '../lib/wearFunnelTelemetry'
import { useWearFunnelLifecycle } from '../hooks/useWearFunnelTelemetry'
import { primaryWearNoteDestination } from '../lib/primaryWearNote'
import { useCatalogDescription } from '../hooks/useCatalogDescription'

const EVENING_CONTEXTS = [
  { key: 'gym', label: 'Gym' },
  { key: 'dinner', label: 'Work dinner' },
  { key: 'date', label: 'Date' },
  { key: 'chill', label: 'Home chill' },
]

// Universal share glyph — replaces the verbose "Share card" text button (founder feedback 2026-07-26).
function ShareIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3v12" /><path d="M8 7l4-4 4 4" /><path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" />
    </svg>
  )
}

function DayArc({ log }) {
  const firstApplication = log?.applications?.find(app => !app.is_reapplication)
    || log?.applications?.[0]
  const applied = firstApplication?.applied_at || log?.created_at
    ? new Date(firstApplication?.applied_at || log.created_at)
    : new Date()
  return (
    <div data-day-arc className="flex items-center gap-2 whitespace-nowrap text-[9px] text-cream/45">
      <span>Applied {applied.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
      <span aria-hidden="true" className="h-0.5 flex-1 rounded-full bg-gradient-to-r from-gold to-sage/30" />
      <span>Check in later</span>
    </div>
  )
}

export function WornHero({ fragrance, log, isPrimary = false, noteEnabled = false, onOpenLayers, onUndo, undoing, onCompliment, onNote, sotdTip, projectionValue, onProjection, projectionSaving, onShare, onMemory }) {
  const shareLoopsEnabled = useFeatureFlag('share_loops')
  const homePrimaryCardEnabled = useFeatureFlag('home_primary_card')
  const primaryWearModelEnabled = useFeatureFlag('primary_wear_model')
  const { description: catalogDescription } = useCatalogDescription(homePrimaryCardEnabled && isPrimary ? fragrance?.catalog_id : null)
  const navigate = useNavigate()
  // Rob, 2026-09-28: tapping today's fragrance opens its fragrance card (the
  // information), not the wear log. "View today's wear" below the card is the
  // door to the log entry.
  const openFragrance = () => navigate(`/fragrance/${fragrance.id}`)
  const handleCardClick = event => {
    // Nested controls keep their own handlers, including controls in inline forms.
    event.stopPropagation()
    const control = event.target.closest('button, a, input, select, textarea, label, summary, [role="button"], [role="link"]')
    if (control && control !== event.currentTarget) return
    openFragrance()
  }
  if (homePrimaryCardEnabled && isPrimary) {
    return <PrimaryWearCard
      fragrance={fragrance}
      log={log}
      description={catalogDescription}
      onOpenLayers={onOpenLayers}
      onOpenFragrance={openFragrance}
      onShare={onShare}
      primaryWearModelEnabled={primaryWearModelEnabled}
    />
  }
  return (
    <section
      role="link"
      tabIndex={0}
      aria-label={`Open the fragrance card for ${fragrance.name}`}
      onClick={handleCardClick}
      onKeyDown={event => {
        if (event.target === event.currentTarget && event.key === 'Enter') {
          event.preventDefault()
          event.stopPropagation()
          openFragrance()
        }
      }}
      className={`${shareLoopsEnabled ? 'relative ' : ''}min-h-11 cursor-pointer rounded-2xl border border-gold/70 bg-gradient-to-br from-charcoal-700 to-charcoal-800 p-3 transition-shadow active:ring-2 active:ring-gold active:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold`}
      data-sotd-hero
      data-hero-fragrance-id={fragrance.id}
      data-wear-log-id={log.id}
    >
      <button onClick={onShare} aria-label={`Share ${fragrance.name}`} className={`${shareLoopsEnabled ? 'absolute right-2 top-2' : 'float-right'} flex min-h-11 min-w-11 items-center justify-center text-gold`}><ShareIcon /></button>
      <p className={`${shareLoopsEnabled ? 'pr-12 ' : ''}text-[10px] font-bold uppercase tracking-[0.16em] text-gold`}>
        {isPrimary ? 'Primary scent for today' : 'Scent of the day (SOTD)'}
      </p>
      {!isPrimary && sotdTip?.show && (
        <p className="mt-1 flex items-center gap-2 text-[10px] text-cream/40">
          <span>SOTD = Scent of the Day</span>
          <button type="button" onClick={sotdTip.onDismiss} className="text-cream/30 underline underline-offset-2 hover:text-cream/60">
            Got it
          </button>
        </p>
      )}
      <div className="mt-3 flex items-center gap-3">
        <div className="w-14 shrink-0"><FragranceImage fragrance={fragrance} /></div>
        <div className="min-w-0 flex-1">
          <h2 className="font-body text-lg leading-tight text-cream">{fragrance.name}</h2>
          <p className="mt-1 text-xs text-cream/45">{fragrance.house}{concentrationLabel(fragrance) ? ` · ${concentrationLabel(fragrance)}` : ''}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[...new Set([log?.occasion, ...(log?.context_tags || []).map(item => item.context_tag)].filter(Boolean))].map(tag => <ContextPill key={tag} tag={tag} className="pill border border-white/10 px-2 py-1 text-[10px] text-cream/55" />)}
            {log?.sprays != null && <span className="pill bg-gold/10 px-2 py-1 text-[10px] text-gold">{log.sprays} sprays</span>}
          </div>
        </div>
      </div>
      {noteEnabled && isPrimary && <button
        type="button"
        onClick={onNote}
        className="mt-2 flex min-h-11 w-full items-center justify-center rounded-xl border border-white/10 bg-transparent px-3 text-sm font-medium text-cream/65 transition-colors hover:border-white/20 hover:text-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
      >Note</button>}
      {(log.layered_with || []).length > 0 && <p className="mt-3 text-sm text-gold">Layered with {log.layered_with.join(' + ')}</p>}
      <div className="mt-3 grid grid-cols-3 gap-1.5" aria-label="Quick capture">
        <Button size="sm" variant="ghost" onClick={onCompliment} className="px-1 text-xs" aria-label="Add a compliment">Compliment{complimentCountForLog(log) > 0 ? ` · ${complimentCountForLog(log)}` : ''}</Button>
        <Button size="sm" variant="ghost" onClick={onMemory}>Memory</Button>
        <Button size="sm" variant="ghost" onClick={onOpenLayers}>Layer</Button>
      </div>
      <div data-sotd-performance className="mt-3 space-y-1">
        <ProjectionCheckin value={projectionValue} onChange={onProjection} disabled={projectionSaving} micro />
        <div className="flex items-center gap-2">
          <p className="shrink-0 text-[9px] text-gold" data-source-badge>Your wear</p>
          <div className="min-w-0 flex-1"><DayArc log={log} /></div>
        </div>
      </div>
      {!isPrimary && <div className="mt-2 flex items-center justify-end">
        <button
          type="button"
          disabled={undoing}
          onClick={onUndo}
          className="flex min-h-11 items-center justify-center gap-2 rounded-lg py-1.5 text-[11px] text-cream/40 transition-colors hover:text-red-300 disabled:opacity-50"
        >
          {undoing && <Spinner size="sm" />}
          {undoing ? 'Removing SOTD…' : 'Undo today’s SOTD'}
        </button>
      </div>}
    </section>
  )
}

export default function HomePage() {
  const primaryWearNoteEnabled = useFeatureFlag('home_primary_wear_note')
  const primaryWearModelEnabled = useFeatureFlag('primary_wear_model')
  const pendingColsEnabled = useFeatureFlag('pending_cols')
  const wearWeatherEnabled = useFeatureFlag('wear_weather')
  const liveWeather = useLiveWeather({ enabled: wearWeatherEnabled })
  // Re-evaluate snapshot freshness every few minutes so an aging reading drops off.
  const [weatherTick, setWeatherTick] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setWeatherTick(tick => tick + 1), 5 * 60 * 1000)
    return () => clearInterval(timer)
  }, [])
  const rotationDoorsEnabled = useFeatureFlag('rotation_doors')
  const wearLogCleanupEnabled = useFeatureFlag('wear_log_tab_cleanup')
  const memoryAnniversaryEnabled = useFeatureFlag('memory_anniversary')
  const anniversarySource = useMemoryAnniversaries(memoryAnniversaryEnabled)
  const nav = useNavigate()
  const location = useLocation()
  const { setHeaderData } = useOutletContext() || {}
  const { fragrances, allFragrances, total, loading, findDuplicate, addFragrance } = useCollection({ showSpecialty: true })
  const anniversaries = useMemo(
    () => (memoryAnniversaryEnabled ? anniversariesForDay({ ...anniversarySource, fragrances }) : []),
    [memoryAnniversaryEnabled, anniversarySource, fragrances],
  )
  const {
    query: catalogQuery,
    setQuery: setCatalogQuery,
    results: catalogResults,
    lines: catalogLines,
    loadLineVariants: loadCatalogLineVariants,
    loading: catalogLoading,
    error: catalogError,
    markSelected: markCatalogSelected,
  } = useCatalogSearch()
  const {
    logs,
    loading: logsLoading,
    addLog,
    updateLog,
    appendCheckin,
    deleteLog,
    ownerUserId,
    getLogSource,
    durationMostWorn,
  } = useWearLog({ limit: 200, enableWeatherSnapshot: wearWeatherEnabled })
  const [query, setQuery] = useState('')
  const catalogSotdState = useRef(createCatalogSotdState())
  const [catalogSaving, setCatalogSaving] = useState(false)
  const [wearTarget, setWearTarget] = useState(null)
  const [recipeLayerLabels, setRecipeLayerLabels] = useState([])
  const [gatewayWearMode, setGatewayWearMode] = useState(null)
  const [loggingAnother, setLoggingAnother] = useState(Boolean(location.state?.logAnother))
  const [shareTarget, setShareTarget] = useState(null)
  const [memoryTarget, setMemoryTarget] = useState(null)
  const [evening, setEvening] = useState('dinner')
  const [layerPickerOpen, setLayerPickerOpen] = useState(false)
  const [projectionSaving, setProjectionSaving] = useState(false)
  const [undoing, setUndoing] = useState(false)
  const [homeError, setHomeError] = useState('')
  const [toast, setToast] = useState('')
  const [gymBoundaryVersion, setGymBoundaryVersion] = useState(0)
  const [wearSession, setWearSession] = useState(null)
  const [pressedWearResultId, setPressedWearResultId] = useState('')
  const [catalogRequestedQuery, setCatalogRequestedQuery] = useState('')
  const [quickAddingId, setQuickAddingId] = useState('')
  const [concentrationPickerLine, setConcentrationPickerLine] = useState(null)
  const searchInputRef = useRef(null)
  const [sotdTipDismissed, setSotdTipDismissed] = useState(() => {
    try {
      return localStorage.getItem('fos_sotd_tip_dismissed') === '1'
    } catch {
      return false
    }
  })
  const dismissSotdTip = () => {
    setSotdTipDismissed(true)
    try {
      localStorage.setItem('fos_sotd_tip_dismissed', '1')
    } catch {
      // Storage unavailable (private mode, quota) — the tip just reappears
      // next visit, which is a harmless degrade, not worth surfacing an error.
    }
  }

  useEffect(() => {
    const now = new Date()
    const nextReset = new Date(now)
    nextReset.setHours(4, 0, 1, 0)
    if (nextReset <= now) nextReset.setDate(nextReset.getDate() + 1)
    const timer = setTimeout(() => setGymBoundaryVersion(value => value + 1), nextReset.getTime() - now.getTime())
    return () => clearTimeout(timer)
  }, [gymBoundaryVersion])

  // A save has to announce itself. Without this the write succeeded silently and
  // users re-tapped, producing duplicate wears (tickets ef459461 / afbb378f).
  const flash = (message) => {
    setToast(message)
    setTimeout(() => setToast(''), 2600)
  }

  const primary = usePrimaryWear({ownerId:ownerUserId,day:todayISO(),logs})
  const selectedPrimaryWear = currentPrimaryWear(primary.selection,primary.basedOnLogs===logs?[]:logs,ownerUserId,todayISO())
  const originalHero = useMemo(() => selectSotdHero({
    logs: primary.available ? primary.dayWears : logs,
    fragrances,
    userId: ownerUserId,
  }), [logs, fragrances, ownerUserId, primary.available, primary.dayWears])
  // The ledger row still occupies today's SOTD slot when display metadata is
  // unavailable. Only the hero goes blank; Home never invites an invalid
  // duplicate SOTD write.
  const heroSelection = primaryHero(selectedPrimaryWear, fragrances) || originalHero
  const todayLog = heroSelection.log
  const wornFragrance = heroSelection.fragrance
  const layeringRecommendationState = useLayeringRecommendations(wornFragrance?.catalog_id)
  const {
    recommendations: sotdPairingRecommendations,
    unownedRecommendations: sotdUnownedPairings,
    loading: sotdPairingsLoading,
    error: sotdPairingsError,
  } = layeringRecommendationState
  const latestProjectionValue = checkinTimeline(todayLog || {}).at(-1)?.value ?? null

  // Hardening only: one array per saved value, not a fresh [] each render.
  // It is NOT the freeze fix. A rendered test found no loop here; the freeze
  // was a stalled download the app waited on forever (see public/sw.js).
  const stableInitialLayeredWith = useMemo(
    () => todayLog?.layered_with || [],
    [todayLog?.id, todayLog?.layered_with]
  )

  const saveProjectionCheckin = async (value) => {
    if (!todayLog || projectionSaving) return
    setProjectionSaving(true)
    setHomeError('')
    try {
      const written = await appendCheckin(todayLog.id, value)
      flash(`Projection ${written.value} saved · ${new Date(written.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`)
    } catch (error) {
      setHomeError(error?.message || 'Could not save the projection check-in.')
    } finally {
      setProjectionSaving(false)
    }
  }
  if (!selectedPrimaryWear) traceSotdHeroRender({
    selection: heroSelection,
    logs: primary.available ? primary.dayWears : logs,
    userId: ownerUserId,
    wearLogSource: getLogSource(todayLog),
  })

  // Recomputed each render (cheap) so the copy softens as the day goes on
  // rather than freezing whatever it was when the page first loaded.
  const promptCopy = sotdPromptCopy()

  const cleanWearQuery = query.trim()
  const collectionResults = useMemo(() => {
    const clean = query.trim()
    if (!clean) return []
    return matchCollectionForWear(fragrances, clean, 20)
  }, [fragrances, query])
  const collectionLines = useMemo(() => {
    if (collectionResults.length === 0) return []
    return groupCollectionByLine(collectionResults)
  }, [collectionResults])
  const catalogExplicitlyRequested = cleanWearQuery.length >= 3
    && catalogRequestedQuery === cleanWearQuery
  const catalogSearchEnabled = !todayLog && cleanWearQuery.length >= 3
    && !loading
    && (collectionResults.length === 0 || catalogExplicitlyRequested)
  const catalogResultsAreCurrent = catalogSearchEnabled
    && catalogQuery.trim() === cleanWearQuery

  useEffect(() => {
    setCatalogQuery(catalogSearchEnabled ? cleanWearQuery : '')
  }, [catalogSearchEnabled, cleanWearQuery, setCatalogQuery])

  const eveningPlan = useMemo(() => {
    if (!wornFragrance) return null
    return eveningRecommendation(evening, wornFragrance, fragrances, logs)
  }, [evening, wornFragrance, fragrances, logs])
  // Today's gym wear, read the same way the SOTD hero reads today's wear, so a
  // gym scent logged from here or the Log gym scent page is reflected on the
  // gym card without a manual refresh.
  const todayGymWear = useMemo(
    () => exactCurrentGymWear(logs, allFragrances, ownerUserId),
    [logs, allFragrances, ownerUserId, gymBoundaryVersion],
  )
  const hasTodaySotd = useMemo(
    () => logs.some(row => row.user_id === ownerUserId
      && row.worn_on === todayISO()
      && (row.wear_label || 'sotd') === 'sotd'
      && !isGymWearLog(row)),
    [logs, ownerUserId],
  )
  const hasTodayWear = useMemo(
    () => logs.some(row => row.user_id === ownerUserId && row.worn_on === todayISO()),
    [logs, ownerUserId],
  )

  useEffect(() => {
    if (!location.state?.logAnother) return
    setLoggingAnother(true)
    nav('/', { replace: true, state: null })
  }, [location.state?.logAnother, nav])

  useEffect(() => {
    const handoff = location.state?.recipeWear
    if (!handoff || loading) return
    const anchor = fragrances.find(item => item.id === handoff.fragranceId)
    if (!anchor) { setHomeError('This stack’s lead fragrance is no longer in your collection.'); nav('/', { replace: true, state: null }); return }
    setGatewayWearMode(todayLog ? 'another' : 'sotd')
    setRecipeLayerLabels(Array.isArray(handoff.layerLabels) ? handoff.layerLabels : [])
    setWearTarget(anchor)
    nav('/', { replace: true, state: null })
  }, [location.state?.recipeWear, loading, fragrances, todayLog, nav])

  const headerMeta = useMemo(() => {
    // Live weather first; a wear-log snapshot only while it is recent (FOS-HOME-WEATHER-STALE-001).
    const temperature = formatFahrenheit(currentDisplayTempC({ liveWeather, logs }))
    return [displayDate(), temperature].filter(Boolean).join(' · ')
  }, [logs, liveWeather, weatherTick])

  useEffect(() => {
    setHeaderData?.({ pathname: '/', meta: headerMeta })
    return () => setHeaderData?.(null)
  }, [headerMeta, setHeaderData])

  useEffect(() => {
    if (loggingAnother) requestAnimationFrame(() => searchInputRef.current?.focus())
  }, [loggingAnother])

  useWearFunnelLifecycle(wearSession)

  const beginWearAttempt = (entryPoint = 'home_hero', meta = null) => {
    if (isWearFunnelSessionActive(wearSession)) return wearSession
    const session = createWearFunnelSession(entryPoint, meta)
    if (!loading && !logsLoading) {
      emitWearFunnelEvent(session, 'search_opened', { meta })
    }
    setWearSession(session)
    return session
  }

  const selectCatalogSotd = async (item) => {
    if(todayLog){setHomeError('Choose a bottle from your collection to add another wear.');return}
    if (catalogSotdState.current.pending) return
    setCatalogSaving(true)
    setHomeError('')
    searchInputRef.current?.blur()
    try {
      const owned = fragrances.find(row => row.catalog_id === item.id) || findDuplicate(item)
      const { bottle } = await saveCatalogSotd(catalogSotdState.current, { item, owned, addFragrance, addLog })
      markCatalogSelected(item)
      setQuery('')
      setLoggingAnother(false)
      flash(`${bottle.name} logged as today’s SOTD`)
      nav('/', { replace: true })
    } catch (error) {
      setHomeError(error?.message || 'Could not log today’s SOTD. Please try again.')
    } finally {
      setCatalogSaving(false)
    }
  }

  const quickAddAdditionalWear = async (fragrance) => {
    if (quickAddingId) return
    const ownedIds = new Set([fragrance.id, ...(fragrance.sizes || []).map(size => size.id)])
    const alreadyLogged = logs.some(row => row.worn_on === todayISO() && ownedIds.has(row.fragrance_id))
    if (alreadyLogged) {
      setQuery('')
      setLoggingAnother(false)
      flash(`${fragrance.name} is already in today’s wear`)
      return
    }
    const session = beginWearAttempt('home_hero', { mode: 'additional_wear' })
    emitWearFunnelEvent(session, 'result_selected', { fragranceId: fragrance.id, meta: { mode: 'additional_wear' } })
    setQuickAddingId(fragrance.id)
    setHomeError('')
    try {
      const row = await addLog(
        { ...buildWearLogEntry(fragrance), wear_label: 'other' },
        { applied_at: new Date().toISOString(), sprays: null },
      )
      emitWearFunnelEvent(session, 'wear_committed', { fragranceId: fragrance.id, meta: { mode: 'additional_wear', wear_label: 'other' } })
      setQuery('')
      setLoggingAnother(false)
      flash(`${row?.fragrance_name || fragrance.name} added to today’s wear`)
      nav('/', { replace: true })
    } catch (error) {
      setHomeError(error?.message || `Could not add ${fragrance.name} to today’s wear.`)
    } finally {
      setQuickAddingId('')
    }
  }

  const selectWearSearchResult = (fragrance, line = null) => {
    // If a line with multiple variants is provided, show concentration picker
    if (line && line.variants && line.variants.length > 1) {
      setConcentrationPickerLine(line)
      return
    }

    if (todayLog) {
      void quickAddAdditionalWear(fragrance)
      return
    }
    const session = beginWearAttempt('home_hero')
    acceptWearSearchSelection({
      fragrance,
      searchInput: searchInputRef.current,
      onResultSelected: item => emitWearFunnelEvent(session, 'result_selected', { fragranceId: item.id }),
      onCollapseResults: () => setQuery(''),
      onClearPressed: () => setPressedWearResultId(''),
      onOpenConfirmation: setWearTarget,
    })
  }

  useEffect(() => {
    if (loading || logsLoading || wearSession) return
    if (wornFragrance && !loggingAnother) return
    const entryPoint = location.state?.logAnother ? 'search_tab' : 'home_hero'
    const session = createWearFunnelSession(entryPoint)
    emitWearFunnelEvent(session, 'search_opened')
    setWearSession(session)
  }, [
    loading,
    logsLoading,
    wornFragrance,
    loggingAnother,
    location.state,
    wearSession,
  ])

  useEffect(() => {
    if (!isWearFunnelSessionActive(wearSession)) return
    if (loading || logsLoading || (wornFragrance && !loggingAnother)) return
    emitWearFunnelEvent(wearSession, 'search_opened')
  }, [wearSession, loading, logsLoading, wornFragrance, loggingAnother])

  const saveEveningContext = async () => {
    if (!todayLog) return
    if (!pendingColsEnabled) {
      return
    }
    try {
      await updateLog(todayLog.id, { evening_context: evening })
    } catch {
      await updateLog(todayLog.id, {
        note: `${todayLog.note || todayLog.notes || ''}\nEvening context: ${evening}`.trim(),
      })
    }
  }

  const confirmSotdFromDeck = (fragrance) => {
    const session = beginWearAttempt('home_hero', { mode: 'sotd_deck' })
    emitWearFunnelEvent(session, 'result_selected', {
      fragranceId: fragrance.id,
      meta: { mode: 'sotd_deck' },
    })
    // Deck choices enter the same confirmation flow as search choices. The
    // confirmation owns both the write and the skippable occasion prompt.
    setWearTarget(fragrance)
  }

  const bumpCompliment = () => {
    if (todayLog) nav(`/log/${todayLog.id}/compliments`)
  }

  const undoSotd = async () => {
    if (!todayLog || undoing) return
    const confirmed = window.confirm(`Remove ${wornFragrance?.name || 'today’s fragrance'} as today’s SOTD?`)
    if (!confirmed) return
    setUndoing(true)
    setHomeError('')
    try {
      await deleteLog(todayLog.id)
      setWearTarget(null)
      setLoggingAnother(false)
      flash('Today’s SOTD removed')
    } catch (error) {
      setHomeError(error?.message || 'Could not remove today’s SOTD.')
    } finally {
      setUndoing(false)
    }
  }

  const openSotdLayers = () => {
    if (!todayLog || !wornFragrance) return
    setLayerPickerOpen(true)
  }

  return (
    <MobileScrollRegion className="mx-auto flex max-w-2xl flex-col gap-5 px-4 py-5 font-body animate-fade-in">
      {wornFragrance && <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-body text-2xl leading-tight text-cream">What I’m wearing</h2>
        </div>
        {loggingAnother && (
          <button
            type="button"
            onClick={() => {
              abandonWearFunnelSession(wearSession, { reason: 'flow_cancel' })
              setLoggingAnother(false)
              setQuery('')
              setPressedWearResultId('')
            }}
            className="text-xs text-cream/45"
          >
            Cancel
          </button>
        )}
      </div>}

      {wornFragrance && (
        <WornHero
          fragrance={wornFragrance}
          isPrimary={Boolean(selectedPrimaryWear)}
          noteEnabled={primaryWearNoteEnabled}
          log={todayLog}
          onShare={() => setShareTarget(wornFragrance)}
          onMemory={() => setMemoryTarget(wornFragrance)}
          onNote={() => {
            const destination = primaryWearNoteDestination(todayLog.id)
            nav(destination.pathname, { state: destination.state })
          }}
          onOpenLayers={openSotdLayers}
          onUndo={undoSotd}
          undoing={undoing}
          onCompliment={bumpCompliment}
          sotdTip={{ show: !sotdTipDismissed, onDismiss: dismissSotdTip }}
          projectionValue={latestProjectionValue}
          onProjection={saveProjectionCheckin}
          projectionSaving={projectionSaving}
        />
      )}

      {wornFragrance && <div className="-mt-3 flex items-center justify-end gap-2" aria-label="Today’s wear actions">
        {wearLogCleanupEnabled && rotationDoorsEnabled && <Link to="/rotation" className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold text-cream/80 transition-all duration-150 hover:border-gold hover:text-gold">Help me pick</Link>}
        <Button size="sm" variant="ghost" onClick={() => nav('/log', { state: { wearLogId: todayLog.id } })} className="px-3 text-xs">View today’s wear</Button>
        <Button size="sm" onClick={() => setLoggingAnother(true)} className="px-3 text-xs">+ Add fragrance</Button>
      </div>}

      {wearLogCleanupEnabled && <TileTaxonomyRow tiles={buildTileTaxonomy(logs, fragrances, { durationRanking: primaryWearModelEnabled ? durationMostWorn : null })} />}

      <GymWearSummary
        wear={todayGymWear?.log?.id===todayLog?.id && wornFragrance ? null : todayGymWear}
        onEdit={row => nav('/log', { state: { wearLogId: row.id, editWear: true } })}
        onOpen={fragrance => nav(`/fragrance/${fragrance.id}`)}
        onShare={fragrance => setShareTarget(fragrance)}
      />

      {(!wornFragrance || loggingAnother) && <section aria-label={wornFragrance ? 'Add another fragrance' : 'Choose today’s fragrance'}>
          {!wornFragrance && !todayLog && (
            // Gate on the log, not the hero: a logged wear whose display
            // metadata is unavailable keeps todayLog with no wornFragrance,
            // and must not be told to choose a scent it already chose.
            <div className="mb-2" data-sotd-prompt>
              <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-gold/80">{promptCopy.label}</h2>
              {promptCopy.helper ? <p className="mt-0.5 text-xs text-cream/45">{promptCopy.helper}</p> : null}
            </div>
          )}
          {rotationDoorsEnabled && (wearLogCleanupEnabled ? !wornFragrance : ownerUserId && !logsLoading && !hasTodayWear) && (
            <Link
              to="/rotation"
              className="mb-3 flex min-h-11 w-full items-center justify-center rounded-xl border border-gold/70 bg-charcoal-800 px-4 py-2 text-center text-sm font-semibold text-gold transition-colors hover:bg-gold/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              {wearLogCleanupEnabled ? 'Help me pick' : 'What should I wear today?'}
            </Link>
          )}
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-gold/70 bg-charcoal-800 px-3 py-3">
            <span className="text-gold">⌕</span>
            <CollectionSearchInput
              ref={searchInputRef}
              collection={fragrances}
              onSelect={selectWearSearchResult}
              value={query}
              onChange={event => {
                const value = event.target.value
                if (value && !query) {
                  const session = beginWearAttempt('home_hero')
                  emitWearFunnelEvent(session, 'query_typed')
                }
                setQuery(value)
              }}
              placeholder="Search my collection"
              className="min-w-0 flex-1 bg-transparent text-sm text-cream outline-none placeholder:text-cream/30"
              autoComplete="off"
            />
          </div>
          {query && (
            <div className="mt-2 overflow-hidden rounded-xl border border-white/8 bg-charcoal-800">
              {loading ? (
                <div className="flex justify-center p-4"><Spinner /></div>
              ) : (
                <>
                  {collectionLines.length > 0 && (
                    <div>
                      <p className="border-b border-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-sage">
                        From your collection
                      </p>
                      {collectionLines.slice(0, 6).map(line => {
                        const representative = line.variants[0]
                        const stats = statsForFragrance(representative, logs)
                        const hasMultipleVariants = line.variants.length > 1
                        return (
                          <button
                            key={representative.id}
                            type="button"
                            onPointerDown={() => setPressedWearResultId(representative.id)}
                            onPointerCancel={() => setPressedWearResultId('')}
                            onPointerLeave={() => setPressedWearResultId('')}
                            disabled={Boolean(quickAddingId)}
                            onClick={() => selectWearSearchResult(representative, hasMultipleVariants ? line : null)}
                            aria-pressed={pressedWearResultId === representative.id}
                            className={`flex w-full items-center gap-3 border-b border-white/5 p-3 text-left transition-colors last:border-b-0 hover:bg-white/5 active:bg-white/10 ${pressedWearResultId === representative.id ? 'bg-gold/10 ring-1 ring-inset ring-gold/35' : ''}`}
                          >
                            <div className="w-10 shrink-0"><FragranceImage fragrance={representative} /></div>
                            <div className="min-w-0 flex-1">
                              <p className="break-words text-sm font-semibold leading-snug text-cream">{line.name}</p>
                              <p className="text-xs text-cream/40">{line.house}</p>
                              {hasMultipleVariants ? (
                                <span className="mt-1 inline-flex rounded-full border border-gold/25 px-2 py-0.5 text-[10px] leading-none text-gold">
                                  {line.variants.length} concentrations
                                </span>
                              ) : concentrationLabel(representative) ? (
                                <span className="mt-1 inline-flex rounded-full border border-gold/25 px-2 py-0.5 text-[10px] leading-none text-gold">
                                  {concentrationLabel(representative)}
                                </span>
                              ) : null}
                            </div>
                            <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] text-sage">{quickAddingId === representative.id ? 'Adding…' : `worn ${stats.wears}x`}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}

                  {collectionResults.length > 0 && !catalogSearchEnabled && cleanWearQuery.length >= 3 && (
                    <button
                      type="button"
                      onClick={() => setCatalogRequestedQuery(cleanWearQuery)}
                      className="w-full px-3 py-2.5 text-left text-xs font-semibold text-gold hover:bg-white/5"
                    >
                      Search the global catalog →
                    </button>
                  )}

                  {catalogSearchEnabled && (
                    <div className={collectionResults.length > 0 ? 'border-t border-gold/20' : ''}>
                      <div className="border-b border-white/5 px-3 py-2">
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-gold">Global catalog</p>
                        <p className="mt-0.5 text-[10px] text-cream/35">Tap a bottle to add it and log today’s SOTD</p>
                      </div>
                      {catalogSaving ? (
                        <p role="status" className="p-3 text-sm text-gold">Adding and logging SOTD…</p>
                      ) : catalogLoading || !catalogResultsAreCurrent ? (
                        <div className="flex justify-center p-4"><Spinner /></div>
                      ) : catalogError ? (
                        <p className="p-3 text-xs text-red-300">{catalogError}</p>
                      ) : (catalogResults.length > 0 || catalogLines.length > 0) ? (
                        <CatalogSearchHierarchy
                          key={cleanWearQuery}
                          query={cleanWearQuery}
                          rows={catalogResults}
                          lines={catalogLines}
                          loadLineVariants={loadCatalogLineVariants}
                          collection={fragrances}
                          findDuplicate={findDuplicate}
                          onSelect={selectCatalogSotd} />
                      ) : (
                        <p className="p-3 text-xs text-cream/45">No catalog fragrances match that search.</p>
                      )}
                    </div>
                  )}

                  {collectionResults.length === 0 && cleanWearQuery.length < 3 && (
                    <p className="p-3 text-xs text-cream/45">Keep typing to search the global catalog.</p>
                  )}
                </>
              )}
            </div>
          )}
      </section>}

      {!primaryWearModelEnabled && ownerUserId && hasTodaySotd && <PrimaryWearControl key={`${ownerUserId}:${todayISO()}`} primary={primary} currentId={todayLog?.id} />}

      {memoryAnniversaryEnabled && <MemoryAnniversaryCard
        items={anniversaries}
        today={anniversarySource.today}
        onOpen={fragrance => nav(`/fragrance/${fragrance.id}`)}
        onWear={fragrance => {
          // Home keeps an idle home_hero session open; close it so this tap is
          // counted under its own entry point, then record the bottle chosen.
          abandonWearFunnelSession(wearSession, { reason: 'memory_anniversary_tap' })
          const session = createWearFunnelSession('memory_anniversary')
          emitWearFunnelEvent(session, 'result_selected', { fragranceId: fragrance.id })
          setWearSession(session)
          setGatewayWearMode(todayLog ? 'another' : 'sotd')
          setWearTarget(fragrance)
        }}
      />}

      <DailyWearHistory
        logs={sessionsForDay(homeHistoryLogs(primary.available ? primary.dayWears : logs, ownerUserId, todayLog, wornFragrance).filter(row => row.id !== todayGymWear?.log?.id), todayISO())}
        fragrances={fragrances}
        onOpenWear={row => nav('/log', { state: { wearLogId: row.id } })}
        onOpenFragrance={fragrance => nav(`/fragrance/${fragrance.id}`)}
      />

      {memoryTarget && <HomeMemorySheet fragrance={memoryTarget} onClose={() => setMemoryTarget(null)} onSaved={() => { setMemoryTarget(null); flash('Memory saved') }} />}

      {homeError && (
        <p role="alert" className="rounded-xl border border-red-400/20 bg-red-400/5 px-3 py-2 text-xs text-red-300">
          {homeError}
        </p>
      )}

      <HomeGateways
        fragrances={fragrances}
        logs={logs}
        liveWeather={liveWeather}
        unavailable={logsLoading || loading || !ownerUserId}
        collectionLoading={loading}
        onChoose={(fragrance, tile) => {
          setGatewayWearMode(tile.kind === 'gym' ? 'gym' : todayLog ? 'another' : 'sotd')
          setWearTarget(fragrance)
        }}
      />
      <Link to="/rediscover" className="inline-flex min-h-11 items-center text-sm text-gold">Rediscover your favorites →</Link>

      <ShareCardSheet
        open={!!shareTarget}
        onClose={() => setShareTarget(null)}
        fragranceId={shareTarget?.id}
      />

      <WearQuickCapture
        open={!!wearTarget}
        fragrance={wearTarget}
        mode={gatewayWearMode || (todayLog ? 'another' : 'sotd')}
        telemetrySession={wearSession}
        initialLayerLabels={recipeLayerLabels}
        onClose={() => {
          abandonWearFunnelSession(wearSession, { reason: 'sheet_dismiss' })
          setWearTarget(null)
          setGatewayWearMode(null)
          setPressedWearResultId('')
          setRecipeLayerLabels([])
        }}
        onSaved={(row) => {
          setWearTarget(null)
          setGatewayWearMode(null)
          setQuery('')
          setLoggingAnother(false)
          setRecipeLayerLabels([])
          flash(`Logged · ${row?.fragrance_name || 'wear'} · ${wearLabelText(row)} · ${appliedAtLabel(row)}`)
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }}
      />

      <ConcentrationPickerSheet
        open={!!concentrationPickerLine}
        line={concentrationPickerLine}
        onClose={() => setConcentrationPickerLine(null)}
        onSelect={(variant) => selectWearSearchResult(variant, null)}
      />

      <LayeringPickerModal
        recommendationState={layeringRecommendationState}
        open={layerPickerOpen}
        anchor={wornFragrance}
        collection={fragrances}
        logs={logs}
        pairingRecommendations={sotdPairingRecommendations}
        unownedPairingRecommendations={sotdUnownedPairings}
        pairingsLoading={sotdPairingsLoading}
        pairingsError={sotdPairingsError}
        initialLayeredWith={stableInitialLayeredWith}
        onClose={() => setLayerPickerOpen(false)}
        onSave={async layeredWith => {
          await updateLog(todayLog.id, { layered_with: layeredWith })
          flash(`Layers saved · ${layeredWith.length} collection item${layeredWith.length === 1 ? '' : 's'}`)
        }}
      />

      {toast && (
        <div
          role="status"
          className="fixed bottom-24 left-1/2 z-50 max-w-xs -translate-x-1/2 rounded-full bg-gold px-4 py-2 text-center text-xs font-bold text-charcoal-900 shadow-lg"
        >
          {toast}
        </div>
      )}

      {logsLoading && <div className="fixed bottom-20 right-4"><Spinner size="sm" /></div>}
    </MobileScrollRegion>
  )
}
